// pfs-icon-picker.ts — set-agnostischer Icon-Wähler (PFS-729 Slice 3).
//
// Grid aus Icon-Kacheln; Auswahl = EIN Key. Wie pfs-radio-group ein Form-Wert-
// Control (field-value-Naht, `.value` get/set, emittiert natives `change`), rendert
// aber SVG-Kacheln statt Radios. SET-AGNOSTISCH: der Consumer reicht die Icon-
// Optionen `{value, svg, label?}` rein (z.B. der @mednet/design-Fachgebiet-Satz) —
// das WC kennt keine Domäne und keinen konkreten Icon-Katalog (dummer Renderer).
//
// API (PFS-662): Kollektion = Property, Skalar/Initial = data-*, Wert = field-value.
//   el.options = [{ value, svg, label? }]   — Icon-Optionen (Property)
//   el.value   (get/set)                    — gewählter Key (field-value-Naht)
//   data-label · data-error · data-hint     — optionales Feld-Chrome
//   data-value (INITIAL)                    — Startwert
// Emittiert natives `change` (bubbles+composed) bei User-Auswahl — .value tragen.
import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { takeOwnProperty } from "../../utils/upgrade-property.js";
import {
  installFieldValue,
  bindSelectionValue,
  readFieldValue,
} from "../../composition/field-value.js";

const STYLES = `
:host { display: block; }
.field { display: block; }
.lbl { display: block; font-size: 0.875rem; font-weight: 500; margin: 0 0 0.3rem; color: var(--pfs-color-on-surface); }
.lbl:empty { display: none; }
.req { color: var(--pfs-color-danger); margin-left: 0.15rem; }
.grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(3.5rem, 1fr)); gap: var(--pfs-space-action, 0.6rem); }
.tile {
  display: flex; flex-direction: column; align-items: center; gap: 0.25rem;
  padding: 0.6rem 0.4rem;
  border: 1px solid var(--pfs-color-outline);
  border-radius: var(--pfs-radius-md, 0.5rem);
  background: var(--pfs-color-surface);
  cursor: pointer; font: inherit; color: inherit;
  transition: border-color 0.12s ease, background-color 0.12s ease;
}
.tile:hover { border-color: var(--pfs-color-primary); background: var(--pfs-color-surface-hover); }
.tile:focus-visible { outline: none; box-shadow: var(--pfs-shadow-focus-ring-strong, 0 0 0 3px rgba(59, 130, 246, 0.45)); }
.tile[aria-checked="true"] { border-color: var(--pfs-color-primary); box-shadow: inset 0 0 0 1px var(--pfs-color-primary); }
.tile .ic { display: inline-flex; font-size: 1.5rem; line-height: 1; color: var(--pfs-color-primary); }
.tile .nm { font-size: 0.7rem; color: var(--pfs-color-on-surface-muted); max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.msg { font-size: 0.8rem; margin: 0.3rem 0 0; }
.msg:empty { display: none; }
.msg.error { color: var(--pfs-color-danger); }
.msg.hint { color: var(--pfs-color-on-surface-muted); }
`;

export interface IconOption {
  value: string;
  svg: string;
  label?: string;
}

export interface PfsIconPickerElement extends HTMLElement {
  options: IconOption[];
  value: string;
}

const normalize = (o: unknown): IconOption => {
  const r = (o ?? {}) as { value?: unknown; svg?: unknown; label?: unknown };
  const value = r.value == null ? "" : String(r.value);
  return {
    value,
    svg: r.svg == null ? "" : String(r.svg),
    label: r.label == null ? undefined : String(r.label),
  };
};

defineComponent({
  tagName: "pfs-icon-picker",
  mixins: [withShadow()],
  // .value ab Konstruktion pre-connect-sicher (PFS-650, uniform mit radio-group).
  created: installFieldValue,
  render(host, ctx) {
    const root = ctx.shadowRoot;
    if (!root) return;
    const style = document.createElement("style");
    style.textContent = STYLES;
    root.appendChild(style);

    const field = document.createElement("div");
    field.className = "field";
    const lblEl = document.createElement("span");
    lblEl.className = "lbl";
    const grid = document.createElement("div");
    grid.className = "grid";
    grid.setAttribute("role", "radiogroup");
    field.append(lblEl, grid);
    root.appendChild(field);
    const msg = document.createElement("p");
    msg.className = "msg";
    root.appendChild(msg);

    let opts: IconOption[] = [];
    const self = host as PfsIconPickerElement;
    const preOptions = takeOwnProperty<IconOption[]>(host, "options");

    // Puffer (field-value) ist die Wahrheit → aria-checked spiegelt ihn; die
    // Reihenfolge options/value ist damit egal (wie radio-group).
    const reflectSelected = (): void => {
      const cur = readFieldValue(host);
      for (const tile of grid.querySelectorAll<HTMLElement>(".tile")) {
        tile.setAttribute("aria-checked", tile.dataset.value === cur ? "true" : "false");
      }
    };

    const commit = bindSelectionValue(host, reflectSelected);

    const renderTiles = (): void => {
      grid.textContent = "";
      for (const o of opts) {
        const tile = document.createElement("button");
        tile.type = "button";
        tile.className = "tile";
        tile.setAttribute("role", "radio");
        tile.dataset.value = o.value;
        if (o.label) tile.title = o.label;
        const ic = document.createElement("span");
        ic.className = "ic";
        ic.innerHTML = o.svg; // vom Consumer geliefertes Icon-Markup (vertrauenswürdig)
        tile.appendChild(ic);
        if (o.label) {
          const nm = document.createElement("span");
          nm.className = "nm";
          nm.textContent = o.label;
          tile.appendChild(nm);
        }
        tile.addEventListener("click", () => {
          commit(o.value);
          host.dispatchEvent(new Event("change", { bubbles: true, composed: true }));
        });
        grid.appendChild(tile);
      }
      reflectSelected();
    };

    Object.defineProperty(host, "options", {
      configurable: true,
      get: () => opts,
      set: (v: unknown) => {
        opts = Array.isArray(v) ? v.map(normalize) : [];
        renderTiles();
      },
    });

    if (preOptions !== undefined) self.options = preOptions;

    const paint = (): void => {
      const labelText = host.dataset.label ?? "";
      lblEl.textContent = labelText;
      if (host.dataset.required != null && labelText) {
        const s = document.createElement("span");
        s.className = "req";
        s.textContent = "*";
        lblEl.appendChild(s);
      }
      const err = (host.dataset.error ?? "").trim();
      const hint = (host.dataset.hint ?? "").trim();
      if (err) {
        msg.textContent = err;
        msg.className = "msg error";
      } else if (hint) {
        msg.textContent = hint;
        msg.className = "msg hint";
      } else {
        msg.textContent = "";
        msg.className = "msg";
      }
    };
    paint();

    const obs = new MutationObserver(paint);
    obs.observe(host, {
      attributes: true,
      attributeFilter: ["data-label", "data-required", "data-error", "data-hint"],
    });
    ctx.cleanup.push(() => obs.disconnect());
  },
});
